  <section
    class="workspace stepper-workspace chart-builder-workspace"
    [class.has-dataset]="!!dataset"
  >
    <div class="chart-builder-controls">
    <!-- ████ Config-driven stepper. Step bodies stay inline as direct <mat-step>
         content (Material projects it eagerly — the reliable path). Step
         visibility + labels come from the JSON layout via isStepVisible()/
         stepLabel(); order is fixed by the template's @if block sequence. ████ -->
    <mat-stepper
      class="chart-stepper"
      [orientation]="layout.stepperOrientation || 'horizontal'"
      [linear]="!!layout.linear"
      #chartStepper
    >

    <!-- ============== Chart type ============== -->
    @if (isStepVisible('chartTypePicker')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('chartTypePicker') }}</ng-template>
        <div class="step-content">
          <div *ngIf="!hasDataset && !isWidgetPreview" class="message warning-message">
            No dataset loaded. Please go to <strong>Rule Builder</strong> to upload data first.
            <button type="button" class="link-btn" (click)="setViewMode('data')">Open Rule Builder →</button>
          </div>

          <div class="step-grid">
            <div class="step-form">
              <div class="control-group">
                <label for="chartType">
                  Chart type
                  <span class="label-info" matTooltip="Pick the visualization style. You can change it any time.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="chartType"
                  #ssSel1
                  class="ss-select"
                  [class.is-open]="!!ssSel1.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="chartTypes"
                  bindLabel="label"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Select the chart"
                  [(ngModel)]="selectedChartTypeOption"
                  (change)="onChartTypeChange()"
                  (clear)="clearChartType()"
                ></ng-select>
              </div>

              <button
                *ngIf="selectedChartTypeOption"
                type="button"
                class="secondary-action"
                (click)="startNewChart()"
              >
                New Chart (clear current chart)
              </button>

              <div *ngIf="validationMessage" class="message warning-message">{{ validationMessage }}</div>

              <!-- <div *ngIf="dataset" class="dataset-summary">
                <div><span>File</span><strong>{{ dataset.fileName }}</strong></div>
                <div><span>Rows</span><strong>{{ dataset.rows.length }}</strong></div>
                <div><span>Columns</span><strong>{{ dataset.columns.length }}</strong></div>
              </div> -->
            </div>

            <div class="step-side">
              <div class="saved-panel">
                <h2>Or load a saved chart</h2>
                <div *ngIf="!savedCharts.length" class="saved-empty">No saved charts yet.</div>
                <button
                  type="button"
                  class="saved-chart"
                  *ngFor="let chart of savedCharts"
                  [class.active]="chart.id === activeSavedChartId"
                  (click)="loadSavedChart(chart)"
                >
                  <span>
                    <strong>{{ chart.name }}</strong>
                    <small>{{ chart.chartType }} · {{ chart.dataset.rows.length }} rows</small>
                  </span>
                  <span
                    class="delete-chart"
                    role="button"
                    tabindex="0"
                    aria-label="Delete saved chart"
                    (click)="deleteSavedChart(chart, $event)"
                  >Delete</span>
                </button>
              </div>
            </div>
          </div>

          <div class="step-nav">
            <span></span>
            <button mat-flat-button color="primary" matStepperNext [disabled]="!hasDataset && !isWidgetPreview">Next: {{ isWidgetPreview ? 'Configure' : 'Map fields' }} →</button>
          </div>
        </div>
      </mat-step>
    }

    <!-- ============== Map fields ============== -->
    @if (isStepVisible('mapFields')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('mapFields') }}</ng-template>

        <!-- Interactive widget configuration (no dataset needed) -->
        <div class="step-content" *ngIf="isWidgetPreview">
          <div class="step-form step-form-wide widget-config-form">
            <div class="control-row">
              <div class="control-group">
                <label for="widgetName">Widget name</label>
                <input id="widgetName" type="text" [(ngModel)]="chartName" placeholder="Name this widget" />
              </div>
              <div class="control-group">
                <label for="widgetLabel">Label</label>
                <input id="widgetLabel" type="text" [(ngModel)]="widgetConfig.label" (ngModelChange)="onWidgetConfigChange()" placeholder="Display label" />
              </div>
            </div>

            <!-- Input box -->
            <div class="control-row" *ngIf="currentPreviewKind === 'input-box'">
              <div class="control-group">
                <label for="wcfgPlaceholder">Placeholder</label>
                <input id="wcfgPlaceholder" type="text" [(ngModel)]="widgetConfig.placeholder" (ngModelChange)="onWidgetConfigChange()" placeholder="Placeholder text" />
              </div>
              <div class="control-group">
                <label for="wcfgDefault">Default value</label>
                <input id="wcfgDefault" type="text" [(ngModel)]="widgetConfig.defaultValue" (ngModelChange)="onWidgetConfigChange()" placeholder="Optional" />
              </div>
            </div>

            <!-- Slider family -->
            <div class="control-row" *ngIf="isSliderWidget">
              <div class="control-group">
                <label for="wcfgMin">Min</label>
                <input id="wcfgMin" type="number" [(ngModel)]="widgetConfig.min" (ngModelChange)="onWidgetConfigChange()" />
              </div>
              <div class="control-group">
                <label for="wcfgMax">Max</label>
                <input id="wcfgMax" type="number" [(ngModel)]="widgetConfig.max" (ngModelChange)="onWidgetConfigChange()" />
              </div>
              <div class="control-group">
                <label for="wcfgStep">Step</label>
                <input id="wcfgStep" type="number" [(ngModel)]="widgetConfig.step" (ngModelChange)="onWidgetConfigChange()" />
              </div>
            </div>
            <div class="control-row" *ngIf="currentPreviewKind === 'slider' || currentPreviewKind === 'range-bidirectional-slider'">
              <div class="control-group">
                <label for="wcfgVal">Default value</label>
                <input id="wcfgVal" type="number" [(ngModel)]="widgetConfig.defaultValue" (ngModelChange)="onWidgetConfigChange()" />
              </div>
            </div>
            <div class="control-row" *ngIf="currentPreviewKind === 'slider-range'">
              <div class="control-group">
                <label for="wcfgRs">Range start</label>
                <input id="wcfgRs" type="number" [(ngModel)]="widgetConfig.rangeStart" (ngModelChange)="onWidgetConfigChange()" />
              </div>
              <div class="control-group">
                <label for="wcfgRe">Range end</label>
                <input id="wcfgRe" type="number" [(ngModel)]="widgetConfig.rangeEnd" (ngModelChange)="onWidgetConfigChange()" />
              </div>
            </div>

            <!-- Button -->
            <div class="control-row" *ngIf="currentPreviewKind === 'button'">
              <div class="control-group">
                <label for="wcfgBtnLabel">Button label</label>
                <input id="wcfgBtnLabel" type="text" [(ngModel)]="widgetConfig.buttonLabel" (ngModelChange)="onWidgetConfigChange()" placeholder="Submit" />
              </div>
              <div class="control-group">
                <label for="wcfgBtnStyle">Style</label>
                <ng-select
                  labelForId="wcfgBtnStyle"
                  class="ss-select"
                  appendTo="body"
                  [items]="buttonStyleOptions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [(ngModel)]="widgetConfig.buttonStyle"
                  (ngModelChange)="onWidgetConfigChange()"
                ></ng-select>
              </div>
            </div>

            <!-- Notes -->
            <div class="control-row" *ngIf="currentPreviewKind === 'notes'">
              <div class="control-group control-group-full">
                <label for="wcfgNotes">Note text</label>
                <textarea id="wcfgNotes" rows="4" [(ngModel)]="widgetConfig.notesText" (ngModelChange)="onWidgetConfigChange()" placeholder="Write your note…"></textarea>
              </div>
            </div>

            <!-- Upload file -->
            <div class="control-row" *ngIf="currentPreviewKind === 'upload-file'">
              <div class="control-group">
                <label for="wcfgAccept">Accepted types</label>
                <input id="wcfgAccept" type="text" [(ngModel)]="widgetConfig.accept" (ngModelChange)="onWidgetConfigChange()" placeholder=".csv,.xlsx" />
              </div>
              <div class="control-group">
                <label class="checkbox-label">
                  <input type="checkbox" [(ngModel)]="widgetConfig.multiple" (ngModelChange)="onWidgetConfigChange()" />
                  Allow multiple files
                </label>
              </div>
            </div>

            <!-- Configurable form -->
            <ng-container *ngIf="currentPreviewKind === 'configurable-form'">
              <div class="control-row">
                <div class="control-group">
                  <label for="wcfgSubmit">Submit label</label>
                  <input id="wcfgSubmit" type="text" [(ngModel)]="widgetConfig.submitLabel" (ngModelChange)="onWidgetConfigChange()" placeholder="Submit" />
                </div>
              </div>
              <div class="widget-repeater">
                <div class="widget-repeater-head">
                  <span>Form fields</span>
                  <button type="button" mat-stroked-button (click)="addWidgetFormField()">+ Add field</button>
                </div>
                <div class="widget-repeater-row" *ngFor="let field of widgetConfig.fields; let i = index">
                  <input type="text" placeholder="Key" [ngModel]="field.key" (ngModelChange)="updateWidgetFormField(i, { key: $event })" />
                  <input type="text" placeholder="Label" [ngModel]="field.label" (ngModelChange)="updateWidgetFormField(i, { label: $event })" />
                  <ng-select
                    class="ss-select"
                    appendTo="body"
                    [items]="widgetFieldTypeOptions"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    [ngModel]="field.type"
                    (ngModelChange)="updateWidgetFormField(i, { type: $event })"
                  ></ng-select>
                  <input
                    type="text"
                    placeholder="Options (comma-sep)"
                    *ngIf="field.type === 'select'"
                    [ngModel]="fieldOptionsText(field)"
                    (ngModelChange)="setFieldOptionsText(i, $event)"
                  />
                  <label class="checkbox-label">
                    <input type="checkbox" [ngModel]="field.required" (ngModelChange)="updateWidgetFormField(i, { required: $event })" /> Req
                  </label>
                  <button type="button" mat-icon-button (click)="removeWidgetFormField(i)" aria-label="Remove field">✕</button>
                </div>
              </div>
            </ng-container>

            <!-- Editable table -->
            <ng-container *ngIf="currentPreviewKind === 'editable-table'">
              <div class="widget-repeater">
                <div class="widget-repeater-head">
                  <span>Columns</span>
                  <button type="button" mat-stroked-button (click)="addWidgetColumn()">+ Add column</button>
                </div>
                <div class="widget-repeater-row" *ngFor="let col of widgetConfig.columns; let i = index">
                  <input type="text" placeholder="Key" [ngModel]="col.key" (ngModelChange)="updateWidgetColumn(i, { key: $event })" />
                  <input type="text" placeholder="Label" [ngModel]="col.label" (ngModelChange)="updateWidgetColumn(i, { label: $event })" />
                  <ng-select
                    class="ss-select"
                    appendTo="body"
                    [items]="['text', 'number']"
                    [clearable]="false"
                    [ngModel]="col.type || 'text'"
                    (ngModelChange)="updateWidgetColumn(i, { type: $event })"
                  ></ng-select>
                  <button type="button" mat-icon-button (click)="removeWidgetColumn(i)" aria-label="Remove column">✕</button>
                </div>
              </div>
            </ng-container>
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" matStepperNext>Next: Customize →</button>
          </div>
        </div>

        <div class="step-content" *ngIf="!isWidgetPreview && dataset">
          <div class="step-form step-form-wide map-fields-form">
            <div class="control-row">
              <div class="control-group" *ngFor="let key of selectedChart.requiredMappings">
                <label [for]="key">{{ mappingLabel(key) }}</label>
                <ng-select
                  [labelForId]="key"
                  #ssSel2
                  class="ss-select"
                  [class.is-open]="!!ssSel2.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="columnsForMapping(key)"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Select column"
                  [ngModel]="mapping[key] || null"
                  (ngModelChange)="mapping[key] = $event || ''; onMappingChange()"
                  (clear)="mapping[key] = ''; onMappingChange()"
                >
                  <ng-template ng-label-tmp let-item="item">
                    {{ item.name }}<span class="ss-option-type">({{ item.type }})</span>
                  </ng-template>
                  <ng-template ng-option-tmp let-item="item">
                    {{ item.name }}<span class="ss-option-type">({{ item.type }})</span>
                  </ng-template>
                </ng-select>
              </div>
            </div>

            <div class="control-row" *ngIf="optionalMappings.length">
              <div class="control-group" *ngFor="let key of optionalMappings">
                <label [for]="key + '-opt'">{{ mappingLabel(key) }} <small class="optional-tag">(optional)</small></label>
                <ng-select
                  [labelForId]="key + '-opt'"
                  #ssSel3
                  class="ss-select"
                  [class.is-open]="!!ssSel3.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="columnsForMapping(key)"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="None"
                  [ngModel]="mapping[key] || null"
                  (ngModelChange)="mapping[key] = $event || ''; onMappingChange()"
                  (clear)="mapping[key] = ''; onMappingChange()"
                >
                  <ng-template ng-label-tmp let-item="item">
                    {{ item.name }}<span class="ss-option-type">({{ item.type }})</span>
                  </ng-template>
                  <ng-template ng-option-tmp let-item="item">
                    {{ item.name }}<span class="ss-option-type">({{ item.type }})</span>
                  </ng-template>
                </ng-select>
              </div>
            </div>

            <div class="control-row">
              <div class="control-group" *ngIf="supportsSeriesField">
                <label for="seriesField">
                  Series / split by
                  <span class="label-info" matTooltip="Optional — splits into one series per value.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="seriesField"
                  #ssSel4
                  class="ss-select"
                  [class.is-open]="!!ssSel4.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="categoryColumns"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="None (single series)"
                  [ngModel]="seriesField || null"
                  (ngModelChange)="seriesField = $event || ''; onSeriesFieldChange()"
                  (clear)="seriesField = ''; onSeriesFieldChange()"
                ></ng-select>
              </div>

              <div class="control-group" *ngIf="selectedChartTypeOption && dataset">
                <label for="aggregation">Aggregation</label>
                <ng-select
                  labelForId="aggregation"
                  #ssSel5
                  class="ss-select"
                  [class.is-open]="!!ssSel5.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="aggregations"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="true"
                  [(ngModel)]="selectedAggregation"
                  (ngModelChange)="onAggregationChange()"
                ></ng-select>
              </div>

             
            </div>

            <div
              class="control-row advanced-options-row"
              *ngIf="supportsSort || supportsTopN || supportsDateGranularity"
            >
              <div class="control-group" *ngIf="supportsSort">
                <label for="sortBy">Sort by</label>
                <ng-select
                  labelForId="sortBy"
                  #ssSel6
                  class="ss-select"
                  [class.is-open]="!!ssSel6.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="dataset?.columns || []"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Default order"
                  [ngModel]="sortBy || null"
                  (ngModelChange)="sortBy = $event || ''; onChartOptionsChange()"
                  (clear)="sortBy = ''; onChartOptionsChange()"
                ></ng-select>
              </div>

              <div class="control-group" *ngIf="supportsSort && sortBy">
                <label for="sortDir">Sort direction</label>
                <ng-select
                  labelForId="sortDir"
                  #ssSel7
                  class="ss-select"
                  [class.is-open]="!!ssSel7.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="sortDirectionOptions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="sortDir"
                  (ngModelChange)="onChartOptionsChange()"
                ></ng-select>
              </div>

              <div class="control-group" *ngIf="supportsTopN">
                <label for="topN">
                  Limit (Top/Bottom N)
                  <span class="label-info" matTooltip="Leave blank for no limit.">ⓘ</span>
                </label>
                <div class="topn-controls">
                  <ng-select
                    #ssSel8
                    class="ss-select topn-dir"
                    [class.is-open]="!!ssSel8.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="topNDirectionOptions"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    [searchable]="false"
                    [(ngModel)]="topNDirection"
                    (ngModelChange)="onChartOptionsChange()"
                  ></ng-select>
                  <input
                    id="topN"
                    type="number"
                    min="1"
                    [(ngModel)]="topN"
                    (ngModelChange)="onChartOptionsChange()"
                    placeholder="All"
                  />
                </div>
              </div>

              <div class="control-group" *ngIf="supportsDateGranularity">
                <label for="dateGranularity">
                  Date granularity
                  <span class="label-info" matTooltip="Buckets the X-axis dates before aggregating.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="dateGranularity"
                  #ssSel9
                  class="ss-select"
                  [class.is-open]="!!ssSel9.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="dateGranularityOptions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="dateGranularity"
                  (ngModelChange)="onChartOptionsChange()"
                ></ng-select>
              </div>
            </div>

            <div
              class="control-row advanced-options-row"
              *ngIf="supportsTooltipColumns || supportsReferenceLine || supportsComparison"
            >
              <div class="control-group" *ngIf="supportsTooltipColumns">
                <label for="tooltipColumns">
                  Tooltip columns
                  <span class="label-info" matTooltip="Extra columns shown on hover.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="tooltipColumns"
                  #ssSel10
                  class="ss-select"
                  [class.is-open]="!!ssSel10.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="tooltipColumnOptions"
                  bindLabel="name"
                  bindValue="name"
                  [multiple]="true"
                  [closeOnSelect]="false"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="None"
                  [(ngModel)]="tooltipColumns"
                  (ngModelChange)="onChartOptionsChange()"
                ></ng-select>
              </div>

              <div class="control-group" *ngIf="supportsReferenceLine">
                <label for="referenceValue">
                  Reference value
                  <span class="label-info" matTooltip="Draws a horizontal target line at this value.">ⓘ</span>
                </label>
                <input
                  id="referenceValue"
                  type="number"
                  [(ngModel)]="referenceValue"
                  (ngModelChange)="onChartOptionsChange()"
                  placeholder="No reference line"
                />
              </div>

              <div class="control-group" *ngIf="supportsComparison">
                <label for="comparisonColumn">
                  Comparison column
                  <span class="label-info" matTooltip="Previous-period column for KPI deltas.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="comparisonColumn"
                  #ssSel11
                  class="ss-select"
                  [class.is-open]="!!ssSel11.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="numericColumns"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="None"
                  [ngModel]="comparisonColumn || null"
                  (ngModelChange)="comparisonColumn = $event || ''; onChartOptionsChange()"
                  (clear)="comparisonColumn = ''; onChartOptionsChange()"
                ></ng-select>
              </div>
            </div>

            <div *ngIf="validationMessage" class="message warning-message">{{ validationMessage }}</div>
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" matStepperNext>Next: Customize →</button>
          </div>
        </div>
        <div class="step-content" *ngIf="!isWidgetPreview && !dataset">
          <div class="empty-state step-empty">Upload data in the previous step to map columns.</div>
        </div>
      </mat-step>
    }

    <!-- ============== Customize (formulas + style) ============== -->
    @if (isStepVisible('styling')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('styling') }}</ng-template>

        <!-- Widgets have no chart styling — configuration lives in step 2 -->
        <div class="step-content" *ngIf="isWidgetPreview">
          <div class="empty-state step-empty">
            This widget is configured in the previous step. Continue to Save.
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" matStepperNext>Next: Save →</button>
          </div>
        </div>

        <div class="step-content" *ngIf="!isWidgetPreview && dataset">
          <div class="styling-panel-wrapper">

            <div class="styling-panel rich-styling">
          <div class="styling-panel-header rich-header">
            <div class="rich-header-text">
              <h2>Formatting &amp; styling</h2>
              <p class="rich-header-sub">Tune visual presentation. Preview updates live.</p>
            </div>
            <button type="button" class="reset-style-btn" (click)="resetStyle()">
              <span class="reset-icon" aria-hidden="true">↺</span> Reset
            </button>
          </div>

          <section class="styling-section">
            <header class="styling-section-header">
              <span class="section-accent section-accent-blue"></span>
              <h3>General</h3>
            </header>
            <div class="control-group">
              <label for="titleOverride">Chart title</label>
              <input
                id="titleOverride"
                type="text"
                [(ngModel)]="chartStyle.titleOverride"
                (ngModelChange)="onStyleChange()"
                placeholder="Leave blank to use chart name"
              />
            </div>
          </section>

          <section class="styling-section" *ngIf="isEChartsPreview">
            <header class="styling-section-header">
              <span class="section-accent section-accent-purple"></span>
              <h3>Appearance</h3>
            </header>

            <div class="control-group">
              <label for="palette">Color palette</label>
              <ng-select
                labelForId="palette"
                #ssSel12
                class="ss-select"
                [class.is-open]="!!ssSel12.isOpen?.()"
                appendTo="body"
                ngClass="ss-dropdown-panel"
                [items]="palettes"
                bindLabel="label"
                bindValue="key"
                [clearable]="false"
                [searchable]="false"
                [(ngModel)]="chartStyle.palette"
                (ngModelChange)="onStyleChange()"
              ></ng-select>
              <div class="palette-swatches palette-swatches-rich">
                <span
                  *ngFor="let color of currentPaletteColors"
                  class="palette-swatch"
                  [style.background-color]="color"
                  [title]="color"
                ></span>
              </div>
            </div>

            <div class="control-group toggle-group">
              <label class="toggle-switch">
                <input type="checkbox" [(ngModel)]="chartStyle.showLegend" (ngModelChange)="onStyleChange()" />
                <span class="toggle-slider"></span>
                <span class="toggle-label">Show legend</span>
              </label>
            </div>

            <div class="control-group" *ngIf="chartStyle.showLegend">
              <label>Legend position</label>
              <div class="segmented-control">
                <button
                  type="button"
                  *ngFor="let opt of legendPositions"
                  class="segmented-option"
                  [class.active]="chartStyle.legendPosition === opt.value"
                  (click)="chartStyle.legendPosition = opt.value; onStyleChange()"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <div class="control-group toggle-group">
              <label class="toggle-switch">
                <input type="checkbox" [(ngModel)]="chartStyle.showLabels" (ngModelChange)="onStyleChange()" />
                <span class="toggle-slider"></span>
                <span class="toggle-label">Show data labels</span>
              </label>
            </div>

            <div class="control-group" *ngIf="chartStyle.showLabels">
              <label>Label mode</label>
              <div class="segmented-control">
                <button
                  type="button"
                  *ngFor="let opt of labelModes"
                  class="segmented-option"
                  [class.active]="chartStyle.labelMode === opt.value"
                  (click)="chartStyle.labelMode = opt.value; onStyleChange()"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </section>

          <section class="styling-section" *ngIf="isEChartsPreview">
            <header class="styling-section-header">
              <span class="section-accent section-accent-teal"></span>
              <h3>Axes</h3>
            </header>
            <div class="styling-row">
              <div class="control-group">
                <label for="xAxisLabel">X-axis title</label>
                <input id="xAxisLabel" type="text" [(ngModel)]="chartStyle.xAxisLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="yAxisLabel">Y-axis title</label>
                <input id="yAxisLabel" type="text" [(ngModel)]="chartStyle.yAxisLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="xAxisLabel2">Second X-axis title</label>
                <input id="xAxisLabel2" type="text" [(ngModel)]="chartStyle.xAxisLabel2" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="yAxisLabel2">Second Y-axis title</label>
                <input id="yAxisLabel2" type="text" [(ngModel)]="chartStyle.yAxisLabel2" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
            </div>
            <div class="styling-row" *ngIf="currentPreviewKind === 'quadrant-bubble'">
              <div class="control-group">
                <label for="quadrantTopLeftLabel">Top-left quadrant</label>
                <input id="quadrantTopLeftLabel" type="text" [(ngModel)]="chartStyle.quadrantTopLeftLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="quadrantTopRightLabel">Top-right quadrant</label>
                <input id="quadrantTopRightLabel" type="text" [(ngModel)]="chartStyle.quadrantTopRightLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="quadrantBottomLeftLabel">Bottom-left quadrant</label>
                <input id="quadrantBottomLeftLabel" type="text" [(ngModel)]="chartStyle.quadrantBottomLeftLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
              <div class="control-group">
                <label for="quadrantBottomRightLabel">Bottom-right quadrant</label>
                <input id="quadrantBottomRightLabel" type="text" [(ngModel)]="chartStyle.quadrantBottomRightLabel" (ngModelChange)="onStyleChange()" placeholder="Auto" />
              </div>
            </div>
          </section>

          <section class="styling-section">
            <header class="styling-section-header">
              <span class="section-accent section-accent-amber"></span>
              <h3>Number format</h3>
              <div class="number-format-preview">
                <span class="nf-preview-label">Sample</span>
                <strong class="nf-preview-value">{{ numberFormatSample }}</strong>
                <span class="nf-preview-neg">{{ numberFormatNegativeSample }}</span>
              </div>
            </header>
            <div class="styling-row">
              <div class="control-group">
                <label for="numberFormat">Format</label>
                <ng-select
                  labelForId="numberFormat"
                  #ssSel13
                  class="ss-select"
                  [class.is-open]="!!ssSel13.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="numberFormats"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="chartStyle.numberFormat"
                  (ngModelChange)="onStyleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label for="numberScale">Scale</label>
                <ng-select
                  labelForId="numberScale"
                  #ssSelScale
                  class="ss-select"
                  [class.is-open]="!!ssSelScale.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="numberScales"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="chartStyle.numberScale"
                  (ngModelChange)="onStyleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label for="decimals">Decimals</label>
                <input
                  id="decimals"
                  type="number"
                  min="0"
                  max="6"
                  step="1"
                  [(ngModel)]="chartStyle.decimals"
                  (ngModelChange)="onStyleChange()"
                />
              </div>
            </div>

            <!-- Secondary (right) Y-axis format — only when a second value axis
                 is in play (yAxis2 mapped, or a bar-line/combo chart). Lets the
                 right axis round/format independently of the left. -->
            <div class="styling-row" *ngIf="hasSecondaryAxis">
              <div class="control-group">
                <label for="numberFormat2">Right axis format</label>
                <ng-select
                  labelForId="numberFormat2"
                  #ssSelNf2
                  class="ss-select"
                  [class.is-open]="!!ssSelNf2.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="numberFormats"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="chartStyle.numberFormat2"
                  (ngModelChange)="onStyleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label for="numberScale2">Right axis scale</label>
                <ng-select
                  labelForId="numberScale2"
                  #ssSelScale2
                  class="ss-select"
                  [class.is-open]="!!ssSelScale2.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="numberScales"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="chartStyle.numberScale2"
                  (ngModelChange)="onStyleChange()"
                ></ng-select>
              </div>
              <div class="control-group">
                <label for="decimals2">Right axis decimals</label>
                <input
                  id="decimals2"
                  type="number"
                  min="0"
                  max="6"
                  step="1"
                  [(ngModel)]="chartStyle.decimals2"
                  (ngModelChange)="onStyleChange()"
                />
              </div>
            </div>

            <!-- Line shape (straight vs curved) — for line/area/combo charts. -->
            <div class="styling-row" *ngIf="supportsLineShape">
              <div class="control-group">
                <label for="lineShape">Line style</label>
                <ng-select
                  labelForId="lineShape"
                  #ssSelLs
                  class="ss-select"
                  [class.is-open]="!!ssSelLs.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="lineShapeOptions"
                  bindLabel="label"
                  bindValue="value"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="chartStyle.lineShape"
                  (ngModelChange)="onStyleChange()"
                ></ng-select>
              </div>
            </div>
          </section>

          <section class="styling-section tooltip-section" *ngIf="isEChartsPreview">
            <header class="styling-section-header tooltip-section-header">
              <span class="section-accent section-accent-pink"></span>
              <h3>Tooltip</h3>
              <label class="toggle-switch toggle-switch-inline">
                <input type="checkbox" [(ngModel)]="chartStyle.tooltipEnabled" (ngModelChange)="onStyleChange()" />
                <span class="toggle-slider"></span>
                <span class="toggle-label">Enabled</span>
              </label>
            </header>

            <ng-container *ngIf="chartStyle.tooltipEnabled">
              <div class="tooltip-presets">
                <button
                  type="button"
                  *ngFor="let preset of tooltipPresets"
                  class="tooltip-preset"
                  [class.active]="chartStyle.tooltipBackground === preset.background"
                  [style.background-color]="preset.background"
                  [style.color]="preset.textColor"
                  [style.border-color]="preset.borderColor"
                  (click)="applyTooltipPreset(preset.key)"
                  [title]="preset.label"
                >
                  {{ preset.label }}
                </button>
              </div>

              <div class="styling-row">
                <div class="control-group">
                  <label for="tooltipTrigger">Trigger</label>
                  <ng-select
                    labelForId="tooltipTrigger"
                    #ssSel14
                    class="ss-select"
                    [class.is-open]="!!ssSel14.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="tooltipTriggers"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    [searchable]="false"
                    [(ngModel)]="chartStyle.tooltipTrigger"
                    (ngModelChange)="onStyleChange()"
                  ></ng-select>
                </div>

                <div class="control-group" *ngIf="resolveCurrentTooltipTrigger() === 'axis'">
                  <label for="axisPointer">Axis pointer</label>
                  <ng-select
                    labelForId="axisPointer"
                    #ssSel15
                    class="ss-select"
                    [class.is-open]="!!ssSel15.isOpen?.()"
                    appendTo="body"
                    ngClass="ss-dropdown-panel"
                    [items]="axisPointerStyles"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    [searchable]="false"
                    [(ngModel)]="chartStyle.tooltipAxisPointer"
                    (ngModelChange)="onStyleChange()"
                  ></ng-select>
                </div>
              </div>

              <div class="styling-row tooltip-colors-row">
                <div class="control-group">
                  <label for="tooltipBg">Background</label>
                  <div class="color-input-wrap">
                    <input type="color" id="tooltipBg" [(ngModel)]="chartStyle.tooltipBackground" (ngModelChange)="onStyleChange()" />
                    <input type="text" class="color-hex" [(ngModel)]="chartStyle.tooltipBackground" (ngModelChange)="onStyleChange()" />
                  </div>
                </div>
                <div class="control-group">
                  <label for="tooltipText">Text color</label>
                  <div class="color-input-wrap">
                    <input type="color" id="tooltipText" [(ngModel)]="chartStyle.tooltipTextColor" (ngModelChange)="onStyleChange()" />
                    <input type="text" class="color-hex" [(ngModel)]="chartStyle.tooltipTextColor" (ngModelChange)="onStyleChange()" />
                  </div>
                </div>
                <div class="control-group">
                  <label for="tooltipBorder">Border</label>
                  <div class="color-input-wrap">
                    <input type="color" id="tooltipBorder" [(ngModel)]="chartStyle.tooltipBorderColor" (ngModelChange)="onStyleChange()" />
                    <input type="text" class="color-hex" [(ngModel)]="chartStyle.tooltipBorderColor" (ngModelChange)="onStyleChange()" />
                  </div>
                </div>
              </div>

              <div class="styling-row">
                <div class="control-group">
                  <label for="tooltipRadius">Corner radius (px)</label>
                  <input
                    id="tooltipRadius"
                    type="number"
                    min="0"
                    max="24"
                    step="1"
                    [(ngModel)]="chartStyle.tooltipBorderRadius"
                    (ngModelChange)="onStyleChange()"
                  />
                </div>
                <div class="control-group">
                  <label for="tooltipFontSize">Font size (px)</label>
                  <input
                    id="tooltipFontSize"
                    type="number"
                    min="9"
                    max="20"
                    step="1"
                    [(ngModel)]="chartStyle.tooltipFontSize"
                    (ngModelChange)="onStyleChange()"
                  />
                </div>
                <div class="control-group">
                  <label for="tooltipDecimals">Round off (decimals)</label>
                  <input
                    id="tooltipDecimals"
                    type="number"
                    min="0"
                    max="6"
                    step="1"
                    [(ngModel)]="chartStyle.tooltipDecimals"
                    (ngModelChange)="onStyleChange()"
                  />
                </div>
              </div>

              <div class="tooltip-preview-wrap">
                <small class="control-hint">Preview:</small>
                <div
                  class="tooltip-preview-bubble"
                  [style.background-color]="chartStyle.tooltipBackground"
                  [style.color]="chartStyle.tooltipTextColor"
                  [style.border-color]="chartStyle.tooltipBorderColor"
                  [style.border-radius.px]="chartStyle.tooltipBorderRadius"
                  [style.font-size.px]="chartStyle.tooltipFontSize"
                >
                  <strong>Sample Category</strong><br/>
                  <span class="tooltip-marker"></span> Series A: 12,450
                </div>
              </div>
            </ng-container>
          </section>

          <section class="styling-section" *ngIf="isKpiPreview">
            <header class="styling-section-header">
              <span class="section-accent section-accent-green"></span>
              <h3>KPI options</h3>
            </header>
            <div class="control-group">
              <label for="comparisonValue">
                Comparison target (optional)
                <span class="label-info" matTooltip="Shown as % delta against the aggregated KPI value.">ⓘ</span>
              </label>
              <input
                id="comparisonValue"
                type="number"
                [(ngModel)]="chartStyle.comparisonValue"
                (ngModelChange)="onStyleChange()"
                placeholder="e.g., 100000"
              />
            </div>
          </section>

          <section class="styling-section" *ngIf="isTablePreview">
            <header class="styling-section-header">
              <span class="section-accent section-accent-green"></span>
              <h3>Table options</h3>
            </header>
            <div class="control-group">
              <label for="tableRowLimit">Row limit</label>
              <input
                id="tableRowLimit"
                type="number"
                min="1"
                max="1000"
                step="1"
                [(ngModel)]="chartStyle.tableRowLimit"
                (ngModelChange)="onStyleChange()"
              />
            </div>
          </section>
        </div>

          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
            <button mat-flat-button color="primary" matStepperNext>Next: Save →</button>
          </div>
        </div>
        <div class="step-content" *ngIf="!isWidgetPreview && !dataset">
          <div class="empty-state step-empty">Upload data in the first step to customize.</div>
        </div>
      </mat-step>
    }

    <!-- ============== Save & wire ============== -->
    @if (isStepVisible('save')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('save') }}</ng-template>

        <!-- Widgets save with just a name — no cross-filter wiring -->
        <div class="step-content" *ngIf="isWidgetPreview">
          <div class="step-form step-form-wide">
            <div class="control-row">
              <div class="control-group">
                <label for="widgetSaveName">Widget name</label>
                <input id="widgetSaveName" type="text" [(ngModel)]="chartName" placeholder="Name this widget" />
              </div>
            </div>
            <div class="save-actions">
              <button type="button" class="primary-action" [disabled]="!canSaveChart" (click)="saveChart()">
                {{ activeSavedChartId ? 'Update Widget' : 'Save Widget' }}
              </button>
              <button
                type="button"
                class="secondary-action"
                [disabled]="!canSaveChart"
                (click)="saveChartAsNew()"
              >
                Save as New
              </button>
              <button type="button" class="secondary-action add-dashboard-action" [disabled]="!activeSavedChartId" (click)="addActiveChartToDashboard()">
                Add to Dashboard →
              </button>
            </div>
            <div *ngIf="saveMessage" class="message success-message">{{ saveMessage }}</div>
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
          </div>
        </div>

        <div class="step-content" *ngIf="!isWidgetPreview && dataset">
          <div class="step-form step-form-wide">
            <div class="control-row">

              <div class="control-group">
                <label for="filterKey">
                  Publish filter key
                  <span class="label-info" matTooltip="Column this chart publishes when a bar/slice is clicked.">ⓘ</span>
                </label>
                <ng-select
                  labelForId="filterKey"
                  #ssSel16
                  class="ss-select"
                  [class.is-open]="!!ssSel16.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="dataset.columns"
                  bindLabel="name"
                  bindValue="name"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="None"
                  [ngModel]="filterKey || null"
                  (ngModelChange)="filterKey = $event || ''; onFilterMetadataChange()"
                  (clear)="filterKey = ''; onFilterMetadataChange()"
                ></ng-select>
                <small class="control-warning" *ngIf="filterKeyMismatchWarning">{{ filterKeyMismatchWarning }}</small>
              </div>

              <div class="control-group">
                <label for="filterListener">Listener charts (cross-filter)</label>
                <ng-select
                  labelForId="filterListener"
                  #ssSel17
                  class="ss-select ss-select--multi"
                  [class.is-open]="!!ssSel17.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="availableListenerCharts"
                  bindLabel="name"
                  bindValue="id"
                  [multiple]="true"
                  [closeOnSelect]="false"
                  [hideSelected]="false"
                  [clearable]="true"
                  [searchable]="true"
                  placeholder="Select listener charts"
                  [(ngModel)]="filterListener"
                  (ngModelChange)="onFilterMetadataChange()"
                  (clear)="filterListener = []; onFilterMetadataChange()"
                ></ng-select>
                <small class="control-hint" *ngIf="!availableListenerCharts.length">Save another chart first to make it a listener.</small>
                <small class="control-warning" *ngFor="let warning of listenerColumnWarnings">{{ warning }}</small>
              </div>

            </div>


            <div class="save-actions">
              <button
                type="button"
                class="primary-action"
                [disabled]="!canSaveChart"
                (click)="saveChart()"
              >
                {{ activeSavedChartId ? 'Update Chart' : 'Save Chart' }}
              </button>

              <button
                type="button"
                class="secondary-action"
                [disabled]="!canSaveChart"
                (click)="saveChartAsNew()"
              >
                Save as New Chart
              </button>

              <button
                type="button"
                class="secondary-action add-dashboard-action"
                [disabled]="!activeSavedChartId"
                (click)="addActiveChartToDashboard()"
              >
                Add to Dashboard →
              </button>
            </div>

            <!-- <div class="generated-preview" *ngIf="generatedConfig">
              <h3>Generated config</h3>
              <pre>{{ generatedConfig }}</pre>
            </div> -->
          </div>
          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back</button>
          </div>
        </div>
        <div class="step-content" *ngIf="!isWidgetPreview && !dataset">
          <div class="empty-state step-empty">Upload data first.</div>
        </div>
      </mat-step>
    }

    </mat-stepper>
    </div>

    <!-- Preview card (only appears when a dataset is loaded) -->
    <section class="preview-panel preview-below-stepper chart-builder-preview" *ngIf="dataset || isWidgetPreview">
      <div class="preview-header">
        <div>
          <h2>{{ selectedChart.label }} preview</h2>
          <p *ngIf="dataset && !isWidgetPreview">Generated from {{ dataset.fileName }}</p>
          <p *ngIf="isWidgetPreview">Interactive widget</p>
        </div>
      </div>

      <div *ngIf="!selectedChartType" class="empty-state">
        Pick a chart type in step 1 to start building.
      </div>

      <div *ngIf="selectedChartType && !isWidgetPreview && !isMappingComplete" class="empty-state">
        Select the required columns in step 2 to preview the chart.
      </div>

      <app-self-service-widget
        *ngIf="isWidgetPreview && selectedChartType"
        class="widget-preview"
        [kind]="currentPreviewKind"
        [config]="widgetConfig"
        [title]="chartName"
      ></app-self-service-widget>

      <div *ngIf="isMappingComplete && validationMessage" class="empty-state">
        {{ validationMessage }}
      </div>

      <app-chart-preview
        *ngIf="isEChartsPreview"
        class="chart-preview"
        [class.is-hidden]="!canRenderPreview"
        [option]="previewOption"
      ></app-chart-preview>

      <div *ngIf="isKpiPreview && canRenderPreview" class="kpi-preview">
        <div class="kpi-card builder-kpi">
          <div class="kpi-label">{{ kpiBuilderDisplay.label }}</div>
          <div class="kpi-value">{{ kpiBuilderDisplay.value }}</div>
          <div
            class="kpi-comparison"
            *ngIf="kpiBuilderDisplay.comparisonText"
            [class.kpi-up]="kpiBuilderDisplay.comparisonDirection === 'up'"
            [class.kpi-down]="kpiBuilderDisplay.comparisonDirection === 'down'"
            [class.kpi-flat]="kpiBuilderDisplay.comparisonDirection === 'flat'"
          >
            {{ kpiBuilderDisplay.comparisonText }}
          </div>
          <div class="kpi-meta">{{ selectedAggregation }} · {{ tableBuilderDisplay.rows.length }} rows</div>
        </div>
      </div>

      <div *ngIf="isTablePreview && canRenderPreview" class="table-preview builder-table">
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th *ngFor="let column of tableBuilderDisplay.columns">{{ column.name }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of tableBuilderDisplay.rows">
                <td *ngFor="let column of tableBuilderDisplay.columns">{{ row[column.name] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-meta">Showing {{ tableBuilderDisplay.rows.length }} of {{ dataset?.rows?.length || 0 }} rows</div>
      </div>

      <div *ngIf="isDrillDownPreview && canRenderPreview" class="drilldown-preview builder-table">
        <lib-generic-drill-down
          class="drilldown-preview-live"
          [data]="drillDownPreviewData"
        ></lib-generic-drill-down>
      </div>
    </section>
  </section>



